'use client'

import React, { FC, ReactElement } from 'react'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { formatUsdPer } from '@/utils/format-usd'
import { NETWORK, API_URL } from '@/utils/constants'
import classNames from 'classnames'
import { Input } from '@/components/ui/input'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import useWallet from '@/utils/hooks/useWallet'
import Outpoint from '@/utils/outpoint'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import isFungible from '@/utils/is-fungible'
import featureFlags from '@/utils/feature-flags'
import {
	getInscriptionCollectionSlugs,
	inscriptionBelongsToCollection
} from '@/utils/inscription-collections'

import useSelect from '../../hooks/useSelect'

import Tabs from './tabs'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import Item from './list-item'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { isSnipeProtectionOffered, protocolFromSlug } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import {
	assertStandardListingAllowed,
	describeSecureListingResult,
	listSecureItems,
	isolateTargetForFailures,
	secureListingFailureMessage,
	trackStandardListingPhase
} from '@/utils/market/secure-listing-client'
import { toastListingSuccess } from '@/utils/market/listing-success-toast'
import { listingNoun } from './listing-noun'
import {
	availabilityForCollection,
	collectionSlugOf,
	planForProtectedFailure,
	protectedErrorCode,
	protectionAssetFromRow,
	resolveListingPlan,
	SNIPE_TEMPORARILY_UNAVAILABLE,
	type ListingPlan,
	type ProtectionAsset
} from '@/utils/market/protection-support'


const MARKET_FEE_RATE = 2.7
const MARKET_FEE_MIN = 1000

function calcStep(n: any) {
	var order = Math.floor(Math.log(n) / Math.LN10 + 0.000000001) // because float math sucks like that
	return Math.pow(10, order) * 1e-1
}

/**
 * The UTXO an inscription row sits on, as `txid:vout`, whichever shape the
 * row came in. The wallet API encodes outpoints as 36-byte hex (txid LE +
 * vout LE), so those are decoded; `txid:vout` strings pass through.
 */
function outpointKey(row: any): string | null {
	const value = row?.utxoOutpoint || row?.outpoint?.outpoint || row?.outpoint
	if (typeof value !== 'string') return null
	if (value.includes(':')) return value
	if (/^[0-9a-f]{72}$/i.test(value)) {
		try {
			const { txid, vout } = Outpoint.decode(value)
			return `${txid}:${vout}`
		} catch {
			return null
		}
	}
	return null
}

type ShapeCode = 'ambiguous_inscription_assets' | 'postage_too_small'
const EMPTY_SHAPE_ACK: Record<string, ShapeCode> = {}

function withShapeAck(asset: ProtectionAsset, code?: ShapeCode): ProtectionAsset {
	if (code === 'ambiguous_inscription_assets') {
		return { ...asset, sharedUtxoCount: Math.max(asset.sharedUtxoCount || 0, 2) }
	}
	if (code === 'postage_too_small') {
		return { ...asset, postageSats: 1 }
	}
	return asset
}

function selectionIdForFailure(rows: any[], failureOutpoint: string, selectedIds: string[]): string | null {
	const row = rows.find(
		(o) =>
			o?.utxoOutpoint === failureOutpoint ||
			o?.outpoint === failureOutpoint ||
			o?.inscription_id === failureOutpoint ||
			o?.id === failureOutpoint
	)
	const id = row?.outpoint
	if (typeof id === 'string' && selectedIds.includes(id)) return id
	return selectedIds.length === 1 ? selectedIds[0] : null
}

function listingToastTarget(slug: string, rows: any[]) {
	return {
		slug,
		inscriptionIds: rows
			.map((row) => row?.inscription_id || row?.id || row?.outpoint)
			.filter(Boolean),
		collectionSlugs: rows.flatMap((row) => getInscriptionCollectionSlugs(row))
	}
}

const CollectionDetailList: FC<{
	marketActions?: React.ReactNode
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	prepared?: { ids: string[]; price: string }
	collectionFilter?: string
}> = (props): ReactElement => {
	const { floorSats, onClose, args, tab, onChangeTab, token, classification, collectionFilter } =
		props

	const { slug } = args
	const { toast } = useToast()
	const currency = isFungible(slug) ? 'sats' : NETWORK
	const step = calcStep(floorSats)

	//
	// Local State
	//

	const [globalPrice, setGlobalPrice] = React.useState(props.prepared?.price || '')
	const [prices, setPrices] = React.useState<Record<string, string | number>>({})
	const [globalSelected, setGlobalSelected] = React.useState(false)

	//
	// Remote Data
	//

	const { exchangeRate } = useExchangeRate()
	const { data: rawData, isLoading } = useTokenOutpoints(token)
	const { data: wallet } = useWallet()
	const isFrozen = slug === 'frozen'

	// How many of the wallet's inscriptions sit on each UTXO, across every
	// collection. Protection moves exactly one inscription, so a shared lot
	// is an unsupported shape: the seller can still list it standard, but
	// only after confirming there is no snipe protection.
	const inscriptionsPerOutpoint = React.useMemo(() => {
		const counts = new Map<string, number>()
		for (const inscription of wallet?.inscriptions || []) {
			const key = outpointKey(inscription)
			if (key) counts.set(key, (counts.get(key) || 0) + 1)
		}
		return counts
	}, [wallet])

	const data = React.useMemo(() => {
		if (!rawData || !Array.isArray(rawData)) return rawData
		let rows = rawData
		if (isFrozen && collectionFilter && collectionFilter !== 'all') {
			rows =
				collectionFilter === 'uncategorized'
					? rows.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
					: rows.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
		}
		if (isFungible(slug)) return rows
		return rows.map((row) => {
			const count = inscriptionsPerOutpoint.get(outpointKey(row) || '') || 0
			return count > 1 ? { ...row, shared_utxo_count: count } : row
		})
	}, [rawData, collectionFilter, isFrozen, slug, inscriptionsPerOutpoint])

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		// Select-all and the slider skip lots already on the market. A shared
		// UTXO stays selectable; listing it requires the shape confirm.
		return data
			.filter((e) => !e.escrow && e.pending_sale !== true)
			.map((e) => e.outpoint)
	}, [data])
	const {
		selected,
		setSelected,
		setSlider,
		onSelect,
		onSelectAll,
		slider,
		onSliderChange,
		allSelected
	} = useSelect(ids)

	const selectedKey = [...selected].sort().join('\n')
	const [shapeState, setShapeState] = React.useState<{
		key: string
		prompt: string | null
		ack: Record<string, ShapeCode>
	}>({ key: '', prompt: null, ack: {} })
	const shapePrompt = shapeState.key === selectedKey ? shapeState.prompt : null
	const shapeAck = shapeState.key === selectedKey ? shapeState.ack : EMPTY_SHAPE_ACK
	const setShapePrompt = React.useCallback(
		(message: string | null) => {
			setShapeState((prev) => ({
				key: selectedKey,
				prompt: message,
				ack: prev.key === selectedKey ? prev.ack : {}
			}))
		},
		[selectedKey]
	)
	const setShapeAck = React.useCallback(
		(update: (prev: Record<string, ShapeCode>) => Record<string, ShapeCode>) => {
			setShapeState((prev) => {
				const prevAck = prev.key === selectedKey ? prev.ack : {}
				return {
					key: selectedKey,
					prompt: prev.key === selectedKey ? prev.prompt : null,
					ack: update(prevAck)
				}
			})
		},
		[selectedKey]
	)

	const appliedPreparation = React.useRef<string>('')
	React.useEffect(() => {
		const prepared = props.prepared
		if (!prepared || !prepared.ids.every((id) => ids.includes(id))) return
		const key = JSON.stringify(prepared)
		if (appliedPreparation.current === key) return
		const frame = requestAnimationFrame(() => {
			appliedPreparation.current = key
			setGlobalPrice(prepared.price)
			setPrices((previous) => ({
				...previous,
				...Object.fromEntries(prepared.ids.map((id) => [id, prepared.price]))
			}))
			setSelected(new Set(prepared.ids))
			setSlider(prepared.ids.length)
		})
		return () => cancelAnimationFrame(frame)
	}, [props.prepared, ids, setSelected, setSlider])

	const floorPrice = React.useMemo(() => String(floorSats || ''), [floorSats])

	React.useEffect(() => {
		if (!globalPrice && floorPrice) {
			requestAnimationFrame(() => {
				setGlobalPrice(floorPrice)
			})
		}
	}, [floorPrice, globalPrice])

	// Pre-select inscription if passed via args
	React.useEffect(() => {
		if (args.inscriptionId && ids.includes(args.inscriptionId) && selected.size === 0) {
			requestAnimationFrame(() => {
				setSelected(new Set([args.inscriptionId]))
			})
		}
	}, [args.inscriptionId, ids, selected.size, setSelected])

	React.useEffect(() => {
		if (globalSelected && selected.size !== ids.length) {
			requestAnimationFrame(() => {
				setGlobalSelected(false)
			})
		}
	}, [globalSelected, ids.length, selected.size])

	//
	// Memos
	//

	const { selectedValue, totalPrice, totalPriceUsd, hasInvalidPrice } = React.useMemo(() => {
		let selectedValue = 0
		let totalPrice = 0
		let hasInvalidPrice = false

		// @ts-ignore
		for (let e of selected.entries()) {
			const id = e[0]
			const amount = parseFloat(data.find((a) => a.outpoint === id)?.amount)
			const rowPrice = parseFloat(String(prices[id] ?? ''))
			selectedValue += amount
			if (isNaN(rowPrice) || rowPrice <= 0) {
				hasInvalidPrice = true
				continue
			}
			totalPrice += rowPrice * amount
		}

		if (currency === NETWORK) {
			totalPrice = totalPrice * 1e8
		}

		const feePercentage = MARKET_FEE_RATE / 100
		const fee = Math.max(totalPrice * feePercentage, MARKET_FEE_MIN * selected.size)

		return {
			selectedValue,
			totalPrice: formatSatoshis(totalPrice),
			totalPriceUsd: formatNumber(((totalPrice / 1e8) * exchangeRate)?.toFixed(2)),
			fee: formatSatoshis(fee),
			feeUsd: formatUsdPer((fee / 1e8) * exchangeRate).usd,
			hasInvalidPrice
		}
	}, [currency, selected, exchangeRate, data, prices])

	//
	// Handlers
	//

	const applyGlobalPriceToAll = React.useCallback(
		(nextGlobal: string) => {
			if (!nextGlobal || ids.length === 0) {
				return
			}

			setSelected(new Set(ids))
			setSlider(ids.length)
			setGlobalSelected(true)
			setPrices(() => {
				const next: Record<string, string> = {}
				for (const id of ids) {
					next[id] = nextGlobal
				}
				return next
			})
		},
		[ids, setSelected, setSlider]
	)

	const clearGlobalSelection = React.useCallback(() => {
		setGlobalSelected(false)
		setSelected(new Set())
		setSlider(0)
		setPrices({})
	}, [setSelected, setSlider])

	const handleGlobalPriceChange = React.useCallback(
		(evt: any) => {
			const newGlobal = evt.target.value
			if (!newGlobal) {
				return
			}

			setGlobalPrice(newGlobal)
			applyGlobalPriceToAll(newGlobal)
		},
		[applyGlobalPriceToAll]
	)

	const handleGlobalPriceToggle = React.useCallback(() => {
		if (globalSelected) {
			clearGlobalSelection()
			return
		}

		const nextGlobal = globalPrice || floorPrice
		if (!nextGlobal) {
			return
		}

		setGlobalPrice(nextGlobal)
		applyGlobalPriceToAll(nextGlobal)
	}, [applyGlobalPriceToAll, clearGlobalSelection, floorPrice, globalPrice, globalSelected])

	const handleGlobalPriceFocus = React.useCallback(() => {
		const nextGlobal = globalPrice || floorPrice
		if (!nextGlobal) {
			return false
		}

		setGlobalPrice(nextGlobal)
		applyGlobalPriceToAll(nextGlobal)
	}, [applyGlobalPriceToAll, floorPrice, globalPrice])

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			return (
				<Item
					key={id}
					slug={slug}
					data={e}
					selected={selected.has(id)}
					onSelect={() =>
						// @ts-ignore
						featureFlags('app::disable_bulk') ? setSelected(new Set().add(id)) : onSelect(id)
					}
					price={prices[id] ?? ''}
					defaultPrice={prices[id] ?? (globalPrice || floorPrice)}
					onPriceChange={(evt) => setPrices((p) => ({ ...p, [id]: evt.target.value }))}
					step={step}
				/>
			)
		},
		[slug, selected, prices, globalPrice, floorPrice, step, setSelected, onSelect]
	)

	const hasTransferable = React.useMemo(() => {
		if (slug.startsWith('tap-') && data?.length) return true
		const id = parseFloat(token.transferable_balance)
		return !isNaN(id) && id !== 0
	}, [token, slug, data?.length])

	const snipeProtection = useSnipeProtection(
		protocolFromSlug(slug, { rune: isRune(slug), alkane: isAlkanes(slug) }),
		slug
	)

	const protectionPreview = React.useMemo(() => {
		const rows = Array.isArray(data) ? data : []
		const chosen = rows.filter((row) => selected.has(row.outpoint))
		if (chosen.length === 0) {
			return { shield: false, note: null as string | null, shapeOnly: false }
		}
		let shield = false
		let note: string | null = null
		let shape = 0
		let other = 0
		for (const row of chosen) {
			const asset = withShapeAck(
				protectionAssetFromRow(row, slug, { rune: Boolean(isRune(slug)), alkane: Boolean(isAlkanes(slug)) }),
				shapeAck[row.outpoint]
			)
			const plan = resolveListingPlan({
				asset,
				availability: snipeProtection.status,
				caps: snipeProtection.caps,
				shapeConfirmed: false
			})
			if (plan.action === 'protect') {
				shield = true
				other += 1
			} else if (plan.action === 'confirm') {
				shape += 1
				note = note || plan.message
			} else if (plan.action === 'standard') {
				if (plan.shape) shape += 1
				else other += 1
				note = note || plan.note
			} else {
				other += 1
				note = note || plan.message
			}
		}
		return { shield, note, shapeOnly: shape > 0 && other === 0 }
	}, [data, selected, shapeAck, snipeProtection.status, snipeProtection.caps, slug])

	const confirmingShape = protectionPreview.shapeOnly || !!shapePrompt

	const handleSubmit = React.useCallback(async (sellerConfirmed = false) => {
		try {
			const ids: string[] = []
			const finalPrices: number[] = []

			// @ts-ignore
			for (const e of selected.entries()) {
				const id = e[0]
				const amount = parseFloat(data.find((o) => o.outpoint === id).amount)
				const unitPrice = parseFloat(String(prices[id] ?? ''))
				if (isNaN(unitPrice) || unitPrice <= 0) {
					throw new Error('Please set a valid price for each selected item')
				}
				ids.push(id)
				const final = parseInt((unitPrice * amount * (currency === NETWORK ? 1e8 : 1))?.toFixed(0))

				finalPrices.push(final)
			}

			const listingTarget = listingToastTarget(
				slug,
				ids.map((id) => data.find((row) => row.outpoint === id)).filter(Boolean)
			)

			const attemptId = crypto.randomUUID()
			const protocol = protocolFromSlug(slug, { rune: isRune(slug), alkane: isAlkanes(slug) })
			const rows = Array.isArray(data) ? data : []
			let availability = snipeProtection.apiStatus
			let caps = snipeProtection.caps
			if (availability === 'loading' || availability === 'probe_failed') {
				const fresh = await isSnipeProtectionOffered(protocol)
				availability = fresh.status
				caps = fresh.caps
			}
			availability = availabilityForCollection(availability, slug)

			type Entry = { id: string; price: number; row: any; asset: ProtectionAsset; plan: ListingPlan }
			const entries: Entry[] = ids.map((id, index) => {
				const row = rows.find((o) => o.outpoint === id)
				const asset = withShapeAck(
					protectionAssetFromRow(row, slug, { rune: Boolean(isRune(slug)), alkane: Boolean(isAlkanes(slug)) }),
					shapeAck[id]
				)
				return {
					id,
					price: finalPrices[index],
					row,
					asset,
					plan: resolveListingPlan({
						asset,
						availability,
						caps,
						shapeConfirmed: sellerConfirmed
					})
				}
			})
			let standard = entries.filter((entry) => entry.plan.action === 'standard')
			const protect = entries.filter((entry) => entry.plan.action === 'protect')
			const confirm = entries.filter((entry) => entry.plan.action === 'confirm')
			const blocked = entries.filter((entry) => entry.plan.action === 'block')

			if (confirm.length > 0 && !sellerConfirmed) {
				const message = confirm[0].plan.action === 'confirm' ? confirm[0].plan.message : ''
				setShapePrompt(message)
				toast({ title: 'Confirm listing without snipe protection', description: message })
				return
			}
			if (blocked.length > 0 && protect.length === 0 && standard.length === 0) {
				const message =
					blocked[0].plan.action === 'block' ? blocked[0].plan.message : SNIPE_TEMPORARILY_UNAVAILABLE
				toast({ title: 'Error', description: message, variant: 'destructive' })
				return
			}

			if (protect.length > 0) {
				try {
					const result = await listSecureItems(
						protect.map((entry) => ({
							inscriptionId:
								entry.row?.inscription_id ||
								(isRune(slug) || isAlkanes(slug) ? undefined : entry.id),
							outpoint: entry.row?.utxoOutpoint || entry.row?.outpoint || entry.id,
							priceSats: entry.price,
							protocol: entry.asset.protocol || protocol,
							inscription: {
								...entry.row,
								collection: { slug: collectionSlugOf(entry.row, slug) || slug }
							}
						})),
						{ attemptId }
					)
					const shapeIds: Record<string, ShapeCode> = {}
					let confirmMessage: string | null = null
					let blockMessage: string | null = null
					for (const failure of result.failures) {
						const next = planForProtectedFailure(failure.code, {
							shapeConfirmed: sellerConfirmed,
							caps,
							provenProtocol: failure.protocol
						})
						const id = selectionIdForFailure(
							rows,
							failure.outpoint,
							protect.map((entry) => entry.id)
						)
						if (next.action === 'confirm' && id && !sellerConfirmed) {
							if (
								failure.code === 'ambiguous_inscription_assets' ||
								failure.code === 'postage_too_small'
							) {
								shapeIds[id] = failure.code
							}
							confirmMessage = next.message
							continue
						}
						if (next.action === 'standard' && id) {
							const entry = protect.find((item) => item.id === id)
							if (entry) {
								standard.push({
									...entry,
									asset: failure.protocol
										? { ...entry.asset, protocol: failure.protocol }
										: entry.asset
								})
							}
							continue
						}
						blockMessage =
							(next.action === 'block' && next.message) || secureListingFailureMessage(failure.code)
					}
					if (Object.keys(shapeIds).length > 0) {
						setShapeAck((prev) => ({ ...prev, ...shapeIds }))
						setShapePrompt(confirmMessage)
					}
					if (confirmMessage && !sellerConfirmed) {
						if (result.listed > 0) {
							toastListingSuccess({
								title: 'Partially listed',
								description: `You listed ${result.listed} with snipe protection.`,
								count: result.listed,
								...listingTarget
							})
						}
						toast({
							title: 'Confirm listing without snipe protection',
							description: confirmMessage
						})
						return
					}
					if (blockMessage && result.listed === 0 && standard.length === 0) {
						toast({ title: 'Error', description: blockMessage, variant: 'destructive' })
						return
					}
					if (result.listed > 0 && standard.length === 0) {
						const summary = describeSecureListingResult(result, listingNoun(classification))
						toastListingSuccess({
							title: summary.title,
							description: summary.description,
							...(summary.failed ? { variant: 'destructive' as const } : {}),
							isolate:
								isRune(slug) || isAlkanes(slug)
									? null
									: isolateTargetForFailures(
											result.failures,
											data.map((o) => ({
												inscriptionId: o.outpoint,
												outpoint: o.utxoOutpoint || o.outpoint
											})),
											slug
									  ),
							count: result.listed,
							...listingTarget
						})
						if (blockMessage) {
							toast({ title: 'Error', description: blockMessage, variant: 'destructive' })
						} else if (!summary.failed) {
							onClose()
						}
						return
					}
					if (blockMessage) {
						toast({ title: 'Error', description: blockMessage, variant: 'destructive' })
					}
				} catch (e) {
					// A protected build can succeed and then throw here (sign or
					// authorize). That used to fall through and create a standard
					// listing 6–15s later. It must not.
					const code = protectedErrorCode(e)
					const next = code
						? planForProtectedFailure(code, { shapeConfirmed: sellerConfirmed, caps })
						: { action: 'block' as const, message: '' }
					if (
						next.action === 'confirm' &&
						!sellerConfirmed &&
						(code === 'ambiguous_inscription_assets' || code === 'postage_too_small')
					) {
						const shapeIds: Record<string, ShapeCode> = {}
						for (const entry of protect) shapeIds[entry.id] = code
						setShapeAck((prev) => ({ ...prev, ...shapeIds }))
						setShapePrompt(next.message)
						toast({ title: 'Confirm listing without snipe protection', description: next.message })
						return
					}
					const message =
						(next.action === 'block' && next.message) ||
						(next.action === 'standard' && next.note) ||
						(next.action === 'confirm' && next.message) ||
						(e as { message?: string })?.message ||
						SNIPE_TEMPORARILY_UNAVAILABLE
					toast({
						title: 'Error',
						description: message,
						variant: next.action === 'standard' ? 'default' : 'destructive'
					})
					return
				}
			}

			if (standard.length === 0) return
			assertStandardListingAllowed(
				standard.map((entry) => entry.asset),
				caps
			)
			const legacyIdList = standard.map((entry) => entry.id)
			const legacyPriceList = standard.map((entry) => entry.price)
			const phase = <T,>(
				event: 'listing_build' | 'listing_sign' | 'listing_authorize',
				run: () => Promise<T>
			): Promise<T> => trackStandardListingPhase(attemptId, protocol, event, run, slug)

			const psbt = await phase('listing_build', () =>
				isRune(slug) || isAlkanes(slug)
					? Wallet.getRuneEscrows(legacyIdList, legacyPriceList)
					: Wallet.getEscrows(legacyIdList, legacyPriceList, {
							forceBulk: featureFlags('app::disable_bulk')
					  })
			)

			let totalCreatedEscrows = 0
			let totalSkippedInputs = 0
			let skippedInputs: any[] = []

			for (const e of Array.isArray(psbt) ? psbt : [psbt]) {
				const { rawtx } = await phase('listing_sign', () => Wallet.signEscrow(e))
				const summary = await phase('listing_authorize', async () => {
					const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
						psbt: rawtx
					})
					return Wallet.assertEscrowResponse(resData)
				})
				totalCreatedEscrows += summary.createdEscrows
				totalSkippedInputs += summary.skippedInputsCount
				skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
			}

			if (totalCreatedEscrows <= 0) {
				throw new Error('No escrows were created')
			}

			if (totalSkippedInputs > 0) {
				const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)
				toastListingSuccess({
					title: 'Partially Listed',
					description: `Created ${formatNumber(
						totalCreatedEscrows
					)} escrows. Skipped ${formatNumber(totalSkippedInputs)} inputs${
						skippedSummary ? `: ${skippedSummary}` : '.'
					}.`,
					count: totalCreatedEscrows,
					...listingTarget
				})
			} else {
				toastListingSuccess({
					title: 'Success!',
					description: `You listed ${formatNumber(
						standard.reduce((sum, entry) => sum + (parseFloat(entry.row?.amount) || 0), 0)
					)} ${listingNoun(classification)}`,
					count: legacyIdList.length,
					...listingTarget
				})
			}
			onClose()
		} catch (e: any) {
			toast({
				title: 'Error',
				description: e?.response?.data?.message || e?.message || 'Unable to list item',
				variant: 'destructive'
			})
		}
	}, [
		slug,
		onClose,
		selected,
		data,
		prices,
		currency,
		toast,
		classification,
		snipeProtection.apiStatus,
		snipeProtection.caps,
		shapeAck,
		setShapeAck,
		setShapePrompt
	])

	const isDisabledForNoPrice = selected.size === 0 || hasInvalidPrice || totalPrice === '0'

	return (
		<>
			{!isFrozen && (
				<>
					<div className="flex px-2 h-7 w-full justify-between items-center">
						<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
						<div className="flex items-center gap-2">
							<p className="text-xxs text-muted-foreground">Set global price ({currency})</p>
							<SelectButton selected={globalSelected} onSelect={handleGlobalPriceToggle} />
							{isLoading ? (
								<div className="flex items-center justify-center h-7 w-24 rounded-md border border-input bg-background">
									<Loading size={14} />
								</div>
							) : (
								<Input
									value={globalPrice || floorPrice}
									step={currency === NETWORK ? step : 1}
									type="number"
									onClick={handleGlobalPriceFocus}
									onFocus={handleGlobalPriceFocus}
									onKeyDown={(evt) => evt.stopPropagation()}
									onChange={handleGlobalPriceChange}
									className={classNames(
										'text-xs py-0 px-2 h-7 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
									)}
								/>
							)}
						</div>
					</div>
					<div className="flex px-2 py-1.5 gap-4">
						<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
						{!featureFlags('app::disable_bulk') && (
							<Slider
								defaultValue={[slider]}
								value={[slider]}
								max={data?.length}
								step={1}
								onValueChange={onSliderChange}
							/>
						)}
					</div>
				</>
			)}
			{(!isFungible(slug) || hasTransferable || isRune(slug) || isAlkanes(slug)) && (
				<>
					<div className="flex justify-between items-center px-2 py-3">
						<div className="flex gap-2 items-center">
							<SelectButton selected={allSelected} onSelect={onSelectAll} />
							<p className="text-xs text-muted-foreground select-none">
								Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
								{data?.length} lots)
							</p>
						</div>
						<p className="text-xs text-muted-foreground select-none">Price ({currency})</p>
					</div>
			{props.marketActions}
					{isLoading && (
						<div className="flex items-center justify-center min-h-[120px]">
							<Loading size={16} />
						</div>
					)}
					{data?.map(renderOutpoint)}
					{!isLoading && data?.length === 0 && !isFungible(slug) && (
						<div className="px-2 py-8 text-center text-xs text-muted-foreground">
							<p>No unlisted items available.</p>
							<button type="button" className="mt-2 font-medium text-primary hover:underline" onClick={() => onChangeTab('delist')}>
								Manage listed items
							</button>
						</div>
					)}
				</>
			)}
			{isFungible(slug) && !hasTransferable && !isRune(slug) && !isAlkanes(slug) && (
				<p className="text-xs flex items-center justify-center h-full mt-8 gap-[2px]">
					You don't have any transferrable balance to list. Please
					<span
						className="text-violet-500 cursor-pointer"
						onClick={() => onChangeTab('split')}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => onChangeTab('split'))}
					>
						split
					</span>
					your balance.
				</p>
			)}
			<Invoice
				open={selected.size > 0}
				onSubmit={() => handleSubmit(confirmingShape)}
				// @ts-ignore
				disabled={isDisabledForNoPrice}
				action={
					isDisabledForNoPrice
						? 'Please set valid prices'
						: confirmingShape
						? 'Confirm list without snipe protection'
						: `List ${formatNumber(selectedValue)} ${classification}`
				}
				items={[
					{ title: 'You Receive', value: `${totalPrice} ${NETWORK} / $${totalPriceUsd}` },
					...snipeProtectionInvoiceItem(protectionPreview.shield, protectionPreview.note)
				]}
			/>
		</>
	)
}

export default CollectionDetailList
